<template>
  <AppLayout>
    <div class="max-w-6xl mx-auto space-y-6">
      <!-- Hero -->
      <section class="move-card p-6 md:p-8 overflow-hidden relative">
        <div
          class="absolute inset-0 opacity-40 pointer-events-none"
          style="background: radial-gradient(circle at top right, var(--move-accent-muted), transparent 55%)"
        />
        <div class="relative flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div class="text-left">
            <p class="text-xs font-bold tracking-widest uppercase move-text-faint mb-2">Workspace</p>
            <h2 class="text-2xl md:text-3xl font-extrabold move-text mb-2">Your boards</h2>
            <p class="move-text-muted max-w-xl">
              Organise operational work into boards, lists, and tasks for your team.
            </p>
          </div>
          <router-link to="/boards/create" class="move-btn-primary shrink-0 self-start md:self-auto">
            <span class="material-icons text-lg">add</span>
            New board
          </router-link>
        </div>
      </section>

      <div v-if="loading" class="flex justify-center py-16">
        <div class="move-spinner" aria-label="Loading boards" />
      </div>

      <div v-else-if="error" class="move-alert-error">
        {{ error }}
      </div>

      <div v-else-if="boards.length === 0" class="move-card move-empty-state py-16">
        <span class="material-icons">view_kanban</span>
        <p class="font-semibold move-text text-lg">No boards yet</p>
        <p class="text-sm move-text-faint mt-1 mb-5">Create your first board to start tracking work.</p>
        <router-link to="/boards/create" class="move-btn-primary">
          <span class="material-icons text-lg">add</span>
          Create board
        </router-link>
      </div>

      <section v-else class="space-y-4">
        <p class="text-sm move-text-faint">
          {{ boards.length }} {{ boards.length === 1 ? 'board' : 'boards' }}
        </p>
        <div class="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 md:gap-5">
          <article
            v-for="board in boards"
            :key="board.id"
            class="move-board-card group"
          >
            <button
              type="button"
              class="move-board-card__main text-left w-full"
              @click="openBoard(board.id)"
            >
              <div class="flex items-start justify-between gap-3 mb-4">
                <div
                  class="inline-flex items-center justify-center w-11 h-11 rounded-xl shrink-0"
                  style="background: var(--move-accent-soft); color: var(--move-accent)"
                >
                  <span class="material-icons">view_kanban</span>
                </div>
                <span
                  v-if="board.is_owner"
                  class="move-kanban-meta"
                >
                  Owner
                </span>
                <span
                  v-else
                  class="text-xs font-semibold move-text-faint px-2 py-1 rounded-full"
                  style="background: var(--move-accent-soft)"
                >
                  Shared
                </span>
              </div>
              <h2 class="text-lg font-bold move-text mb-1 group-hover:text-[var(--move-accent)] transition-colors">
                {{ board.name }}
              </h2>
              <p class="text-sm move-text-muted mb-3">Owner: {{ board.ownerName }}</p>
              <div class="flex items-center gap-3 text-xs move-text-faint">
                <span class="inline-flex items-center gap-1">
                  <span class="material-icons text-sm">group</span>
                  {{ board.members?.length || 0 }} members
                </span>
                <span v-if="board.updated_at" class="inline-flex items-center gap-1">
                  <span class="material-icons text-sm">schedule</span>
                  {{ formatRelative(board.updated_at) }}
                </span>
              </div>
            </button>
            <div class="move-board-card__actions">
              <button
                type="button"
                class="move-btn-primary flex-1 !py-2"
                @click="openBoard(board.id)"
              >
                Open board
              </button>
              <button
                v-if="isBoardOwner(board)"
                type="button"
                class="move-btn-ghost !px-3 !text-[var(--move-danger)] !border-[var(--move-danger)]/30 hover:!bg-[var(--move-danger-soft)]"
                aria-label="Delete board"
                @click.stop="confirmDeleteBoard(board)"
              >
                <span class="material-icons text-lg">delete</span>
              </button>
            </div>
          </article>
        </div>
      </section>

      <MoveConfirmDialog
        v-model="showDeleteBoardModal"
        title="Delete board"
        subtitle="This action cannot be undone."
        :detail-title="boardToDelete?.name || ''"
        detail-body="All lists and tasks on this board will be removed permanently."
        detail-tone="danger"
        confirm-label="Delete board"
        confirm-icon="delete"
        busy-label="Deleting…"
        :busy="isDeleting"
        :error="deleteError"
        @confirm="deleteBoard"
        @cancel="closeDeleteBoardModal"
      />
    </div>
  </AppLayout>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { moveApi } from '../services/moveApi'
import AppLayout from '../layouts/AppLayout.vue'
import MoveConfirmDialog from '../components/MoveConfirmDialog.vue'

const router = useRouter()
const boards = ref([])
const loading = ref(true)
const error = ref('')
const showDeleteBoardModal = ref(false)
const boardToDelete = ref(null)
const isDeleting = ref(false)
const deleteError = ref('')

const formatRelative = (dateString) => {
  if (!dateString) return ''
  const date = new Date(dateString)
  const diffMs = Date.now() - date.getTime()
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))
  if (diffDays <= 0) return 'Updated today'
  if (diffDays === 1) return 'Updated yesterday'
  if (diffDays < 7) return `Updated ${diffDays}d ago`
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

const fetchBoards = async () => {
  try {
    loading.value = true
    error.value = ''
    const { data } = await moveApi.listBoards()
    boards.value = (data.data || []).map((board) => ({
      ...board,
      ownerName: board.owner_name || 'Unknown'
    }))
  } catch (err) {
    console.error('Error in fetchBoards:', err)
    error.value = 'Error loading boards'
  } finally {
    loading.value = false
  }
}

const openBoard = (boardId) => {
  router.push(`/board/${boardId}`)
}

const isBoardOwner = (board) => board.is_owner === true

const confirmDeleteBoard = (board) => {
  boardToDelete.value = board
  deleteError.value = ''
  showDeleteBoardModal.value = true
}

const closeDeleteBoardModal = () => {
  if (isDeleting.value) return
  showDeleteBoardModal.value = false
  boardToDelete.value = null
  deleteError.value = ''
}

const deleteBoard = async () => {
  if (!boardToDelete.value || isDeleting.value) return

  try {
    isDeleting.value = true
    deleteError.value = ''
    await moveApi.deleteBoard(boardToDelete.value.id)
    boards.value = boards.value.filter((b) => b.id !== boardToDelete.value.id)
    showDeleteBoardModal.value = false
    boardToDelete.value = null
  } catch (err) {
    console.error('Error deleting board:', err)
    deleteError.value = err.response?.data?.message || 'Failed to delete board. Please try again.'
  } finally {
    isDeleting.value = false
  }
}

onMounted(fetchBoards)
</script>

<script>
export default {
  name: 'BoardsView'
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/icon?family=Material+Icons');

.material-icons {
  font-family: 'Material Icons', sans-serif;
  font-weight: normal;
  font-style: normal;
  line-height: 1;
  display: inline-block;
}
</style>
